/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { SortDirection } from "ag-grid-community";
import { Observable } from "rxjs";
import { LogoutResponse } from "../models/http/user/logout-response.model";
import { Role } from "../models/http/user/role.model";
import { UserInfo } from "../models/http/user/user-info.model";
import { PagedResponse } from "../models/ui/paged-response.model";
import { TableResponse } from "../models/ui/table-response.model";
import { UsersHttpService } from "./http/users-http.service";

@Injectable({
	providedIn: "root"
})
export class UsersService {
	constructor(private _usersHttpService: UsersHttpService) {}

	/**
	 * Retrieves the list of user roles.
	 *
	 * @returns An observable that emits an array of user roles.
	 */
	public getRoles(): Observable<Role[]> {
		return this._usersHttpService.getUserRoles();
	}

	/**
	 * Retrieves the user information.
	 *
	 * @returns An observable that emits the user information.
	 */
	public getUserInfo(): Observable<UserInfo> {
		return this._usersHttpService.getUserInfo();
	}

	/**
	 * Retrieves a paginated list of users based on the specified criteria.
	 *
	 * @param tenantId - The ID of the tenant to which the users belong.
	 * @param page - The page number to retrieve.
	 * @param size - The number of users per page.
	 * @param sort - (Optional) The field by which to sort the users.
	 * @param filter - (Optional) A filter string to apply to the user search.
	 * @param role - (Optional) An array of roles to filter the users by.
	 * @param applications - (Optional) An array of applications to filter the users by.
	 * @param direction - (Optional) The direction of the sort, either "asc" or "desc".
	 * @returns An Observable that emits a PagedResponse containing UserInfo objects.
	 */
	public getUsers(
		tenantId: string,
		page: number,
		size: number,
		sort?: string,
		filter?: string,
		role?: string[],
		applications?: string[],
		direction?: SortDirection
	): Observable<TableResponse<PagedResponse<UserInfo>>> {
		return this._usersHttpService.getUsers(
			tenantId,
			page,
			size,
			sort,
			filter,
			role,
			applications,
			direction
		);
	}

	/**
	 * Adds a new user to the specified tenant.
	 *
	 * @param tenantId - The ID of the tenant to which the user will be added.
	 * @param user - The user information to be added.
	 * @returns An Observable that emits the ID of the newly added user.
	 */
	public addUser(tenantId: string, user: UserInfo): Observable<string> {
		return this._usersHttpService.addUser(tenantId, user);
	}

	/**
	 * Retrieves a user by their ID.
	 *
	 * @param tenantId - The ID of the tenant to which the user belongs.
	 * @param userId - The ID of the user to retrieve.
	 * @returns An Observable that emits the full user details.
	 */
	public getUserById(tenantId: string, userId: number): Observable<UserInfo> {
		return this._usersHttpService.getUserById(tenantId, userId);
	}

	/**
	 * Updates the user information for a specific tenant.
	 *
	 * @param tenantId - The ID of the tenant to which the user belongs.
	 * @param userId - The ID of the user to be updated.
	 * @param user - The updated user information.
	 * @returns An Observable that emits a string indicating the result of the update operation.
	 */
	public updateUser(tenantId: string, userId: number, user: UserInfo): Observable<string> {
		return this._usersHttpService.updateUser(tenantId, userId, user);
	}

	/**
	 * Deletes a user based on the provided tenant ID and user ID.
	 *
	 * @param tenantId - The ID of the tenant to which the user belongs.
	 * @param userId - The ID of the user to be deleted.
	 * @returns An Observable that emits a string indicating the result of the deletion operation.
	 */
	public deleteUser(tenantId: string, userId: number): Observable<string> {
		return this._usersHttpService.deleteUser(tenantId, userId);
	}

	/**
	 * Logs out the current user.
	 *
	 * @returns An Observable that emits the identity provider logout URL to navigate to.
	 */
	public logout(): Observable<LogoutResponse> {
		return this._usersHttpService.logout();
	}
}
